import { composeGid } from '@shopify/admin-graphql-api-utilities'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { gql } from '../../../../core/graphql/gql'
import { storefrontClient } from '../../../../core/shopify/client'
import type { Product } from '../../types'
import { parseShopifyProduct, ShopifyProduct } from './parse-shopify-product'

export const GetProductQuery = gql`
  query getProduct($id: ID!) {
    product(id: $id) {
      id
      title
      images(first: 10, sortKey: POSITION) {
        nodes {
          altText
          url
        }
      }
      metafields: metafield(namespace: "custom", key: "add_ons") {
        namespace
        key
        value
      }
      designerCollaboration: metafield(namespace: "custom", key: "designer_collaboration") {
        namespace
        key
        value
      }
      tags
      variants(first: 1, sortKey: POSITION) {
        nodes {
          id
          sku
          price: priceV2 {
            amount
            currencyCode
          }
        }
      }
    }
  }
`

export const GetProductResponse = z.object({
  data: z.object({
    product: ShopifyProduct
  })
})

export const useProduct = (productId: string | null): Product | null => {
  const { data: product } = useQuery(
    ['loading', 'get-product', productId],
    async (): Promise<Product | null> => {
      if (productId == null) {
        return null
      }

      const {
        data: { product: shopifyProduct }
      } = await GetProductResponse.parseAsync(
        await storefrontClient(GetProductQuery, {
          id: composeGid('Product', productId)
        })
      )
      return parseShopifyProduct(shopifyProduct)
    },
    {
      onError(err) {
        console.error('Product cannot be fetched', err)
      },
      keepPreviousData: true
    }
  )

  if (product == null) {
    return null
  }

  return product
}
